import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Router from 'next/router';
import moment from 'moment';
import { isEmpty, last, map, sortBy } from 'lodash';
import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import { ZIndex } from '@styles/z-index';
import { mq } from '@styles/breakpoints';
import { mixins } from '@styles/mixins';
import { Theme, ThemeProps } from '@theme/base';
import {
  TourAvailability,
  TourAvailabilityHotel,
  TourAvailabilityHotelYear,
  TourAvailabilityResult,
  TourAvailabilityYears,
  TourCheapest
} from '@model/tours';
import { getCheapestTour, getMinPrice } from '@model/tours/get-cheapest-tour';
import {
  ISO_8601_DATE_FORMAT,
  LONG_DAY_MONTH_YEAR_FORMAT,
  MONTH_FORMAT_SHORT,
  YEAR_FORMAT,
  TripTypes
} from '@model/common';
import { Occupancy } from '@model/state/deal-finder-state';
import { NO_AIRPORT_CODE } from '@model/iceberg/airports/no-airport';
import { performNavigate } from '@state/slices/app';
import {
  fetchFromTourReference,
  getFromTourReference,
  getTourFilters,
  getTourReferences,
  getTourReferencesError,
  setTourReferences
} from '@state/slices/tours';
import { getShowModalPayload, ModalType, showModal } from '@state/slices/modal/modalSlice';
import { getTourExtrasUrl } from '@util/tours';
import { getAdults } from '@util/deal-finder';
import { TestId } from '@components/test-ids';
import { ContentTabs } from '@components/common/content-tabs';
import { DropdownOption } from '@components/common/dropdown';
import { Airports } from '@components/price-calendar/filters/airports/Airports';
import { Occupancy as OccupancySelector } from '@components/price-calendar/filters/occupancy/Occupancy';
import { useI18NextContext } from '@components/hooks';
import { SkeletonElement, SkeletonVariant } from '@components/common/skeleton-loading';
import { StarRating } from '@components/common/star-rating';
import { InfoCardModal } from '@components/common/modal';
import { PrivateTourModalData } from '@components/modal/private-tours/PrivateTourEnquiryModal';
import { NoResults } from '@components/search-results/tour-results-grid';
import { TourPricing } from './TourPricing';
import { TourPricingCalendar } from './TourPricingCalendar';
import Grid from '@mui/material/Grid';
import { MOCK_TOUR_AVAILABILITY_HOTEL } from '@mock/iceberg/tours';
import { TextMarkdown } from '@components/common/description';
import { getNumberOfGuests } from '@util/common';
import { dispatch } from '@state/store';

/* ***************** *
 *       Types       *
 * ***************** */
export interface TourPricingTabsProps {
  theme?: Theme;
  availabilityResult: TourAvailabilityResult;
  testId?: string;
  tripType: TripTypes;
  tourName: string;
  month?: string;
  startDate: string;
  searchToken?: string;
  airports: Array<string>;
  occupancy: Array<Occupancy>;
  onChangeFilters: ({ airport, adults }: { airport: string; adults: string }) => void;
  isGhosting?: boolean;
  cheapest?: TourCheapest;
  showCalendar?: boolean;
}

export interface TourAvailabilityPayload {
  guests: number;
  email: string;
  airport: string;
  deepLink: string;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container = styled.div(({ theme }: ThemeProps) => ({
  ...mixins(theme).card,
  overflow: 'unset'
}));

const FiltersContainer = styled.div(({ theme }: ThemeProps) => ({
  columnGap: theme.custom.spacing.medium,
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.custom.spacing.medium,
  div: {
    flexGrow: 1
  }
}));

const contentTabsStyling = {
  ['.MuiAppBar-root']: {
    marginBottom: 0,
    zIndex: ZIndex.CONTENT
  }
};

const HotelRowContainer: any = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  paddingLeft: theme.custom.spacing.large,
  marginTop: theme.custom.spacing.medium,

  [mq.medium]: {
    flexWrap: 'nowrap'
  }
}));

const HotelName = styled.p(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h5 as any),
  flex: '1 0 auto',
  width: '100%',
  fontWeight: 'bold',
  margin: 0,
  marginRight: theme.custom.spacing.medium,

  [mq.medium]: {
    flex: 'none',
    width: 'auto'
  }
}));

const MoreInfo: any = styled.a(({ theme }: ThemeProps) => ({
  ...mixins(theme).link,
  ...(theme.custom.typography.paragraph as any),
  paddingLeft: 10
}));

const RatingContainer = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  marginBottom: theme.custom.spacing.medium,

  [mq.medium]: {
    height: theme.custom.spacing.large
  }
}));

/* ****************************** *
 *    TourPricingTabsComponent    *
 * ****************************** */

export const TourPricingTabsComponent = ({
  testId,
  availabilityResult,
  tripType,
  tourName,
  searchToken = '',
  month,
  startDate,
  occupancy,
  airports,
  onChangeFilters,
  isGhosting,
  cheapest,
  showCalendar
}: TourPricingTabsProps) => {
  const t = useI18NextContext();

  const { results, variation, filters } = availabilityResult;
  const { adults: allowedAdults } = filters;

  const tourReferences = useSelector(getTourReferences);
  const tourReferencesError = useSelector(getTourReferencesError);
  const fromTourReference = useSelector(getFromTourReference);
  const { adults: selectedAdults, airport: selectedAirport } = useSelector(getTourFilters);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [departureDate, setDepartureDate] = useState('');
  const [filterOccupancy, setFilterOccupancy] = useState(
    selectedAdults && (!allowedAdults || allowedAdults.includes(selectedAdults))
      ? [{ adults: selectedAdults, children: [] }]
      : occupancy
  );
  const [showHotelModal, setShowHotelModal] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (tourReferencesError) {
      showError(tourReferencesError);
    } else if (departureDate && (searchToken || fromTourReference) && tourReferences?.length > 0) {
      dispatch(
        performNavigate(
          getTourExtrasUrl({
            ...Router.query,
            tripType,
            searchToken: searchToken || fromTourReference,
            productPath: variation.path,
            departureDate,
            airports: filterAirport,
            adults: getAdults(filterOccupancy),
            month,
            tourReferences: tourReferences.join(',')
          })
        )
      );
    }
  }, [tourReferences, tourReferencesError, departureDate, fromTourReference]);

  if (!filters || !results) {
    return <NoResults />;
  }
  const { adults, airports: filterAirports = [] } = filters;
  const availableAirports = filterAirports.filter(({ code }) => code !== NO_AIRPORT_CODE);

  const cheapestMonth =
    cheapest && cheapest.year && results[cheapest.year]
      ? (results[cheapest.year][0]?.months || {})[cheapest.month]
      : undefined;
  const cheapestTour: TourAvailability = cheapestMonth
    ? cheapestMonth.find(({ price }) => price.amount === cheapest?.price.amount) || cheapestMonth[0]
    : getCheapestTour(results, month);

  const getLeadInMonth = (results: TourAvailabilityYears, initialYear: string) => {
    if (results && results[initialYear]) {
      return (results[initialYear] || Object.values(results)[0])[0];
    }
  };
  const getLeadInTour = (leadInYear: TourAvailabilityHotelYear, initialMonth: string) => {
    if (!leadInYear.months) {
      return;
    }
    const monthAvailability = leadInYear.months[initialMonth] || Object.values(leadInYear.months)[0];
    return getMinPrice(monthAvailability);
  };

  const searchParams = new URLSearchParams(window.location.search);
  const alternativeDate = searchParams.get('alternativeDate');
  const startDateValue = alternativeDate ? alternativeDate : startDate;

  let initialMonth: string;
  let initialYear: string;
  if (startDateValue) {
    [initialYear] = startDateValue.split('-');
    initialMonth = moment(startDateValue).format(MONTH_FORMAT_SHORT);
  } else {
    if (cheapest && cheapest.year) {
      const { year, month } = cheapest;
      initialYear = year.toString();
      initialMonth = month;
    } else {
      const { departureDate }: TourAvailability = cheapestTour;
      [initialYear] = departureDate.split('-');
      initialMonth = moment(departureDate).format(MONTH_FORMAT_SHORT);
    }
  }

  const leadInYear = getLeadInMonth(results, initialYear);
  const leadInTour = leadInYear && getLeadInTour(leadInYear, initialMonth);
  const leadInTourYear = moment(leadInTour?.departureDate).format(YEAR_FORMAT);
  const initialAirport = airports.length === 1 ? airports[0] : leadInTour?.airports[0].code;
  const isPrivateTours = tripType === TripTypes.PRIVATE_TOURS;

  const resultYears = Object.entries(results);
  const tabLabels = Object.values(resultYears).map(([year]) => {
    return year;
  });
  const initialTabIndex: number = tabLabels.indexOf(initialYear || leadInTourYear) || 0;

  const defaultSelectedAirport = availableAirports.find(({ code }) => code === (selectedAirport || initialAirport));
  const [filterAirport, setFilterAirport] = useState((defaultSelectedAirport || availableAirports[0])?.code);

  const getHotel = () => {
    return leadInYear || leadInTour ? leadInYear.hotel : { ...MOCK_TOUR_AVAILABILITY_HOTEL };
  };

  const toggleHotelModal = (hotelName?: string) => {
    setShowHotelModal(hotelName && hotelName === showHotelModal ? undefined : hotelName);
  };

  const showError = (errorCode?: string) => {
    setIsSubmitting(false);
    dispatch(showModal(getShowModalPayload(ModalType.TOURS_ERROR, { errorCode, tripType })));
  };

  const updateAirports = (airport) => {
    if (!isGhosting) {
      setFilterAirport(airport);
      onChangeFilters({ airport, adults: getAdults(filterOccupancy) });
    }
  };

  const updateGuests = (guests) => {
    if (!isGhosting) {
      setFilterOccupancy(guests);
      onChangeFilters({ airport: filterAirport, adults: getAdults(guests) });
    }
  };

  const privateTourDateDropdownOptions: Array<DropdownOption> =
    tripType === TripTypes.PRIVATE_TOURS
      ? resultYears.reduce((acc: any, [year, avail]: [string, Array<TourAvailabilityHotelYear>]) => {
          const yearAvail: Array<DropdownOption> = Object.keys(avail[0].months).map((month: string) => ({
            value: `${t(`common__label--${month.toLowerCase()}`)} ${year}`,
            name: `${t(`common__label--${month.toLowerCase()}`)} ${year}`
          }));

          return [...acc, ...yearAvail];
        }, [])
      : [];

  const openEnquiryModal = (payload: TourAvailability) => {
    const { departureDate } = payload;
    const modalData: PrivateTourModalData = {
      tourName,
      selectedDate: moment(departureDate).format(LONG_DAY_MONTH_YEAR_FORMAT)
    };
    dispatch(showModal(getShowModalPayload(ModalType.PRIVATE_TOUR_ENQUIRY, modalData)));
  };

  const handleOnSubmit = async (payload: TourAvailability) => {
    if (isPrivateTours && (!payload.reference || payload.soldOut || !payload.price.amount)) {
      openEnquiryModal(payload);
    } else {
      if (!isSubmitting) {
        const { departureDate, reference, airports } = payload;
        setDepartureDate(moment(departureDate).format(ISO_8601_DATE_FORMAT));

        if (reference) {
          const airportCode = airports[0].code;
          if (airportCode !== filterAirport) {
            setFilterAirport(airportCode);
          }
          setIsSubmitting(true);
          if (searchToken) {
            dispatch(setTourReferences({ packageReferences: [reference], searchToken }));
          } else {
            dispatch(fetchFromTourReference(reference));
          }
        } else {
          showError();
        }
      }
    }
  };

  /* *** RENDERERS *** */
  const renderTabsContent = () =>
    resultYears.map(([year, availabilityHotelYears]: [string, Array<TourAvailabilityHotelYear>]) => {
      return (
        <TourPricing
          key={year}
          year={year}
          availabilityHotelYears={Object.values(availabilityHotelYears)}
          onSubmit={handleOnSubmit}
          tripType={tripType}
          tourName={tourName}
          privateTourDateOptions={privateTourDateDropdownOptions}
          isDisabled={isSubmitting}
          initialMonth={initialMonth}
          initialYear={initialYear}
          totalGuests={getNumberOfGuests(filterOccupancy)}
        />
      );
    });

  const renderHotelDescription: (hotel: TourAvailabilityHotel) => JSX.Element = ({
    rating,
    description
  }: TourAvailabilityHotel) => (
    <>
      <RatingContainer>
        <StarRating rating={rating} size={20} />
      </RatingContainer>
      <TextMarkdown description={description} />
    </>
  );

  const renderHotel = ({ name, rating, gallery }: TourAvailabilityHotel) => {
    return (
      <HotelRowContainer>
        <HotelName>{name}</HotelName>
        <StarRating rating={rating} />
        {!!name && (
          <MoreInfo
            onClick={() => {
              toggleHotelModal(name);
            }}
          >
            {t('common__label--more-info')}
          </MoreInfo>
        )}
        <InfoCardModal
          title={name}
          gallery={map(gallery, 'url')}
          show={showHotelModal === name}
          onDismiss={toggleHotelModal}
          description={renderHotelDescription(getHotel())}
        />
      </HotelRowContainer>
    );
  };

  return (
    <Container data-testid={testId || TestId.tourPricing.tabsComponent}>
      {!isEmpty(getHotel()) && renderHotel(getHotel())}
      <FiltersContainer>
        <Grid item xs={12} md={6}>
          {isGhosting && availableAirports?.length === 0 && (
            <SkeletonElement variant={SkeletonVariant.RECTANGLE} width={'100%'} height={40} />
          )}
          {!isGhosting && availableAirports?.length > 0 && (
            <Airports airport={filterAirport} airports={availableAirports} onChange={updateAirports} />
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {isGhosting && adults?.length === 0 && (
            <SkeletonElement variant={SkeletonVariant.RECTANGLE} width={'100%'} height={40} />
          )}
          {!isGhosting && adults?.length > 0 && (
            <OccupancySelector
              occupancy={filterOccupancy}
              onChange={updateGuests}
              maxRooms={1}
              maxGuests={last(sortBy(adults))}
              adultsOnly
              disabled={isPrivateTours && adults.length === 1}
            />
          )}
        </Grid>
      </FiltersContainer>
      {!isGhosting && showCalendar && (
        <TourPricingCalendar
          results={results}
          initialMonth={initialMonth}
          initialYear={initialYear}
          isLoading={!!isGhosting || isSubmitting}
          cheapest={cheapestTour}
          onSubmit={handleOnSubmit}
          isPrivateTours={isPrivateTours}
        />
      )}
      {isGhosting && showCalendar && (
        <>
          <SkeletonElement variant={SkeletonVariant.RECTANGLE} width={'100%'} height={655} />
          <FiltersContainer>
            <Grid item xs={12} md={6}>
              <SkeletonElement variant={SkeletonVariant.RECTANGLE} width={'100%'} height={40} />
            </Grid>
            <Grid item xs={12} md={6}>
              <SkeletonElement variant={SkeletonVariant.RECTANGLE} width={'100%'} height={40} />
            </Grid>
          </FiltersContainer>
        </>
      )}
      {isGhosting && !showCalendar && (
        <SkeletonElement variant={SkeletonVariant.RECTANGLE} width={'100%'} height={813} />
      )}
      {!isGhosting && !showCalendar && (
        <ContentTabs tabLabels={tabLabels} styles={contentTabsStyling} initialTabIndex={initialTabIndex}>
          {renderTabsContent()}
        </ContentTabs>
      )}
    </Container>
  );
};

export const TourPricingTabs = withTheme(TourPricingTabsComponent);
